/** @module csi/heading/Url */
define([
	"dojo/_base/declare",
	"dojo/has",
	"dojo/dom-construct",
	"csi",
	"csi/action",
	"csi/comm",
	"csi/comm/Aspera",
	"csi/def/Action",
	"csi/heading/_base",
	"csi/heading/Attribute",
	"csi/heading/FileBrowse",
	"csi/heading/FileReset",
	"csi/html",
	"csi/locale",
	"csi/service/fileService",
	"csi/state/rootStore",
	"csi/utility/coreUtility",
	"csi/utility/fileUtility",
	"csi/widget/MaterialIcon",
	"csi/widget/MessageDialog",
	"csi/xml"
], function(declare, has, domConstruct, csi, csiAction, comm, Aspera, Action, csiHeading, Attribute, FileBrowse, FileReset, html, locale, {getFileStatusAttributeId}, {browserStore}, {isDefined}, {formatFileSize}, MaterialIcon, MessageDialog, xml) {

	const ICON_CSS = "dijitInline csiActionIcon far fa-file-pdf";

	function _isPdfAttribute(h) {
		return h.m_id && (h.m_id.indexOf("PDF") !== -1);
	}

	return declare(Attribute, {
		defaultValue: csi.EMPTY_URL,
		queryOps: csiHeading.QUERY_OPS[xml.ATTR_STRING],
		type: xml.ATTR_URL,

		/** @property {boolean} bCanReset - to determine add FileReset action to tooltip */
		bCanReset: false,

		// String: default text for empty filenames
		_default: null,

		constructor: function() {
			// summary: create action for attributes of this type
			this._default = locale.resource("Url_Text");
		},

		canLink: function() {
			// summary: files are already anchors
			return false;
		},

		/**
		 * If filtering by file, then use attribute holding file name for filtering
		 */
		getQuerySpec: function(ctx, h, spec) {
			// restrict filtering on PDF attributes
			if (_isPdfAttribute(h)) {
				return null;
			}

			var qs = this.inherited(arguments);
			qs.id = xml.idGetFile(qs.id);
			qs.relative = false;
			qs.ren = csiHeading.getRenderer(xml.ATTR_STRING);
			qs.type = xml.ATTR_STRING;
			return qs;
		},

		isTabStop: function(/*Context*/ctx, /*Heading*/h) {
			// summary: edits with dialog so skip when tabbing
			return false;
		},

		/**
		 * @override
		 * Return the File Herf (link to active download).
		 */
		_getFileHref: function(ctx, value, fileName) {
			return comm.getFileHref(value, false, fileName);
		},

		/**
		 * Returns whether the node has a valid file
		 * @param {Object} value
		 * @param {Context} ctx
		*/
		hasValidFile: function(value, ctx) {
			return xml.isValidURL(value);
		},

		toHtmlStatic: function(/*Context*/ctx, /*Heading*/h, /*DOMNode*/refNode, /*ActionData*/actionData) {
			// summary: render value for attribute in HTML - allow not only centric URLs, but also plain web URLs for Insight support
			var value = this._getValue(ctx, h);

			// In cases where the file is broken up into chunks, the File attribute is centric: but FileChunks will have a JSON string
			if (this.hasValidFile(value, ctx)) {
				var name = this.toValue(ctx, h);
				var bPdf = _isPdfAttribute(h) || (name && name.indexOf(".pdf") !== -1);
				var bPdfIcon = bPdf && !name;
				var fileName = name;

				if (!fileName && bPdf) {
					fileName = this._getPdfTitle(ctx);
				}

				let href = this._getFileHref(ctx, value, fileName);

				// don't show link if file vault not present
				if (!ctx.canLink() || has("csi-no-files")) {
					href = undefined;
				}

				if (href) {
					const anchor = html.a({
						"class": bPdfIcon ? null : html.CSS_BROWSE,
						href,
						onclick: (evt) => {
							html.stopPropagation();
							// If Aspera is enabled, prevent default (browser download) and use Aspera download instead
							if (Aspera.isEnabled()) {
								evt.preventDefault();
								Aspera.downloadFile(value);
								return false;
							}
						},
						target: "_blank"
					}, refNode);

					if (bPdfIcon) {
						html.create("span", {
							"class": ICON_CSS,
							innerText: " "
						}, anchor);
					} else {
						html.text(anchor, name || this._default);
					}
				} else if (bPdfIcon) {
					html.create("span", {
						"class": ICON_CSS,
						innerText: " ",
						style: "cursor:initial",
					}, refNode);
				} else {
					html.text(refNode, name || this._default);
				}

				// show the file size if we have a node in the cache with the same URL as file URL
				if (ctx.view?.showFileSizes) {
					// the file size might not be in the cache yet, but browserStore stores file sizes on upload so it might have it
					const fileSize = ctx.cache.getNode(value)?.FileSize || browserStore.getFileUploadSize(ctx.getNodeURL(), h.m_id);

					if (fileSize) {
						html.text(refNode, ` (${formatFileSize(fileSize)})`);
					}
				}
			}

			const node = ctx.getNode();
			const status = node && this._getProgressBarStatus(node, h);

			if (status == null && !ctx.view?.showFileSizes) {
				// Can't remove entries from the file upload map when showing file sizes since the map contain file size data
				browserStore.deleteFromFileUploadMap(ctx.getNodeURL(), h.m_id);
			}

			return !!refNode.firstChild;
		},

		toTooltip: function(/*Context*/ctx, /*Heading*/h, /*Tooltip*/tooltip) {
			// summary: add actions to browse and reset file
			this.inherited(arguments);

			// Only create abort action if abort controller exists for the node
			if (isDefined(this._getProgressBarStatus(ctx.getNode(), h)) && browserStore.getFileUploadController(ctx.getNodeURL(), h.m_id)) {
				tooltip.addAction(this._mixinTooltipAction(this._createAbortAction(ctx), h), 1);
			} else if (this.bCanReset) {
				tooltip.addAction(this._mixinTooltipAction(new FileReset(), h), 1);
			}
		},

		toValue: function(/*Context*/ctx, /*Heading*/h) {
			// summary: return string to display for file
			return this.toFilterValue(ctx, h) || "";
		},

		_getAction: function() {
			// summary: actions are in tooltip
			return new FileBrowse();
		},

		_getPdfTitle: function(/*Context*/ctx) {
			// summary: return title for PDF viewer tab when filename is not known
			return locale.getNodeTypeName(ctx.getNode());
		},

		_toFilterValue: function(/*Context*/ctx, /*Heading*/h, /*Any*/value) {
			// summary: if container, just return the index because we don't have filename by index; otherwise return filename stored in separate attribute
			if (ctx.keyValue == null) {
				return ctx.getNode()[xml.idGetFile(h.m_id)];
			}

			return ctx.keyValue;
		},

		/**
		 * @override
		 * Prevent FileBrowse tooltip action while upload is in progress
		 */
		_getRenderAction(ctx, h) {
			const ad = this.inherited(arguments);

			if (!!ad && isDefined(this._getProgressBarStatus(ctx.getNode(), h))) {
				return null;
			}

			return ad;
		},

		/**
		 * Create an action that allows the file upload to be aborted
		 * @param {Context} ctx
		 * @return  {Action}
		 */
		_createAbortAction(ctx) {
			const action = Object.assign(new Action(), {
				classController: "csi/heading/AbortUploadController",
				m_id: "AbortFileUpload",
				confirmIcon: MessageDialog.ICON_ERROR,
				confirmAction: MessageDialog.BUTTON_CONFIRM
			});
			return action;
		},

		_toHtmlStatus(ctx, h, refNode, status) {
			const domNode = domConstruct.create("span", {"class": csiHeading.CSS_PROGRESS_CONTAINER}, refNode);
			this.inherited(arguments, [ctx, h, domNode, status]);

			const nodeURL = ctx.getNode().$URL;
			const fileSize = ctx.view?.showFileSizes && browserStore.getFileUploadSize(nodeURL, h.m_id);

			if (fileSize) {
				html.text(refNode, ` (${formatFileSize(fileSize)})`);
			}

			// If there's no abortController in the browserStore, then there's no file upload that can be cancelled
			if (browserStore.getFileUploadController(nodeURL, h.m_id)) {
				new MaterialIcon({
					icon: 'close',
					onClick: () => {
						csiAction.invoke(ctx.view, domNode, this._mixinTooltipAction(this._createAbortAction(ctx), h));
					}
				}).placeAt(domNode);
			}
		},

		/**
		 * @override
		 * Handle status attribute for custom attributes
		 */
		getIdStatus(h) {
			return getFileStatusAttributeId(h.m_id);
		}
	});
});